import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import api from './services/api';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import Login from './pages/Login';
import SsoLogin from './pages/SsoLogin';
import Users from './pages/Users';
import Teams from './pages/Teams';
import TaskDetail from './pages/TaskDetail';
import Companies from './pages/Companies';
import { isAdmin, isManager } from './utils/role';
import Avatar from './components/Avatar';

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    }
  }, []);

  const changePassword = async () => {
    setPwdError(''); setPwdSuccess('');
    if (!pwdForm.currentPassword || !pwdForm.newPassword) { setPwdError('All fields are required'); return; }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) { setPwdError('New passwords do not match'); return; }
    if (pwdForm.newPassword.length < 6) { setPwdError('Password must be at least 6 characters'); return; }
    setPwdSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      setPwdSuccess('Password changed successfully!');
      setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowChangePwd(false); setPwdSuccess(''); }, 1500);
    } catch (e) {
      setPwdError(e.response?.data?.error || 'Failed to change password');
    } finally { setPwdSaving(false); }
  };

  const links = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    ...(isManager() ? [{ path: '/teams', label: 'Teams', icon: '👥' }] : []),
    ...(isAdmin() ? [{ path: '/users', label: 'Users', icon: '🔑' }] : []),
    ...(isAdmin() ? [{ path: '/companies', label: 'Companies', icon: '🏢' }] : []),
  ];

  const logout = () => {
    // If the user arrived via the CLTS portal (SSO), end the portal session too:
    // the SSO /logout sends multi-app users back to the launcher and single-app
    // users to Login. If they logged in directly at pm.cloudleaders.co.in, show the PM login.
    const viaPortal = localStorage.getItem('loginSource') === 'portal';
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginSource');
    if (viaPortal) {
      window.location.href = 'https://app.cloudleaders.co.in/logout';
    } else {
      navigate('/login');
    }
  };

  return (
    <aside style={{
      width: 'var(--sidebar-width)', height: '100vh', position: 'fixed', left: 0, top: 0,
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: '#fff', display: 'flex', flexDirection: 'column', zIndex: 100
    }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.5 }}>CLTS PM</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Center</div>
      </div>

      <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
        {links.map(l => (
          <Link key={l.path} to={l.path} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            borderRadius: 8, color: location.pathname === l.path ? '#fff' : '#94a3b8',
            background: location.pathname === l.path ? 'rgba(59,130,246,0.2)' : 'transparent',
            marginBottom: 4, fontSize: 14, fontWeight: 500, textDecoration: 'none',
            transition: 'all 0.15s'
          }}>
            <span style={{ fontSize: 18 }}>{l.icon}</span> {l.label}
          </Link>
        ))}

        {/* Projects Section */}
        {projects.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 16px', marginBottom: 4 }}>Projects</div>
            {projects.map(p => {
              const isActive = location.pathname === `/projects/${p.id}`;
              const statusDot = { PLANNING: '#3b82f6', ACTIVE: '#10b981', ON_HOLD: '#f59e0b', COMPLETED: '#6b7280', CANCELLED: '#ef4444' }[p.status] || '#6b7280';
              return (
                <Link key={p.id} to={`/projects/${p.id}`} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
                  borderRadius: 8, color: isActive ? '#fff' : '#94a3b8',
                  background: isActive ? 'rgba(59,130,246,0.2)' : 'transparent',
                  marginBottom: 2, fontSize: 13, fontWeight: 500, textDecoration: 'none',
                  transition: 'all 0.15s'
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusDot, flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.projectName}</span>
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {showChangePwd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 4, color: '#0f172a' }}>Change Password</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Logged in as <strong>{user.username}</strong></p>
            {pwdError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{pwdError}</div>}
            {pwdSuccess && <div style={{ background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{pwdSuccess}</div>}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Current Password</label>
              <input type="password" value={pwdForm.currentPassword} onChange={e => setPwdForm({ ...pwdForm, currentPassword: e.target.value })} style={{ width: '100%' }} placeholder="Enter current password" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>New Password</label>
              <input type="password" value={pwdForm.newPassword} onChange={e => setPwdForm({ ...pwdForm, newPassword: e.target.value })} style={{ width: '100%' }} placeholder="Min 6 characters" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Confirm New Password</label>
              <input type="password" value={pwdForm.confirmPassword} onChange={e => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })} style={{ width: '100%' }} placeholder="Re-enter new password" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={changePassword} disabled={pwdSaving} style={{ flex: 1 }}>{pwdSaving ? 'Saving...' : 'Change Password'}</button>
              <button className="btn-ghost" onClick={() => { setShowChangePwd(false); setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setPwdError(''); }} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Avatar name={user.fullName || 'User'} size={34} fontSize={14} />
          <div>
            <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{user.fullName || 'User'}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{user.role}</div>
          </div>
        </div>
        <button onClick={() => { setShowChangePwd(true); setPwdError(''); setPwdSuccess(''); }} style={{
          width: '100%', padding: '6px 12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
          color: '#94a3b8', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginBottom: 6
        }}>🔑 Change Password</button>
        <button onClick={logout} style={{
          width: '100%', padding: '6px 12px', background: 'rgba(255,255,255,0.1)',
          color: '#94a3b8', borderRadius: 6, fontSize: 12, border: 'none', cursor: 'pointer'
        }}>Logout</button>
      </div>
    </aside>
  );
}

function Layout({ children }) {
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <main style={{ marginLeft: 'var(--sidebar-width)', flex: 1, padding: 24, minHeight: '100vh' }}>
        {children}
      </main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/sso" element={<SsoLogin />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/projects" element={<Navigate to="/" />} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/tasks/:taskId" element={<ProtectedRoute><TaskDetail /></ProtectedRoute>} />
        <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
        <Route path="/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}
